import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";

import { PlayerAIClient, type PlayerAIClientOptions } from "./client.js";

export interface PlayerAIProviderProps {
  /** V2 HTTP/SSE connection settings. */
  options: PlayerAIClientOptions;
  children: ReactNode;
}

interface PlayerAIContextValue {
  client: PlayerAIClient;
}

const PlayerAIContext = createContext<PlayerAIContextValue | null>(null);

/**
 * Mounts a singleton `PlayerAIClient` for the session. One instance feeds
 * every surface (rail / fullscreen / popover) and survives route changes.
 *
 * Tenant + actor identity remain in the authenticated BFF session. The scope
 * passed to `usePlayerAI(scope)` contains only private/workspace routing data.
 *
 * Disposes the client when the provider unmounts (e.g. logout flushes the
 * shell). Per-conversation SSE lifecycles are managed by `usePlayerAI`.
 */
export function PlayerAIProvider({ options, children }: PlayerAIProviderProps) {
  const client = useMemo(
    () => new PlayerAIClient(options),
    // Recreate the client when any of the connection-shaping settings change.
    [
      options.baseUrl,
      options.reconnectBaseMs,
      options.getCsrfToken,
      options.fetchImpl,
    ],
  );

  useEffect(() => () => client.disconnect(), [client]);

  const value = useMemo<PlayerAIContextValue>(() => ({ client }), [client]);

  return (
    <PlayerAIContext.Provider value={value}>
      {children}
    </PlayerAIContext.Provider>
  );
}

/** Read the `PlayerAIClient` mounted by the nearest `<PlayerAIProvider>`. */
export function usePlayerAIClient(): PlayerAIClient {
  const ctx = useContext(PlayerAIContext);
  if (!ctx) {
    throw new Error(
      "usePlayerAIClient must be used inside <PlayerAIProvider>. Mount the provider at the app shell.",
    );
  }
  return ctx.client;
}

/** Read the mounted client when Player AI is available to an embedding shell. */
export function useOptionalPlayerAIClient(): PlayerAIClient | null {
  return useContext(PlayerAIContext)?.client ?? null;
}
